import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import InquiryForm from '@/components/InquiryForm';
import ReviewSection from '@/components/ReviewSection';
import { 
  ArrowLeft, Star, MapPin, Phone, Mail, Globe, Heart, 
  ChevronLeft, ChevronRight, Loader2, MessageCircle, Store 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { buildVendorEmailHref, buildVendorWhatsAppHref } from '@/lib/vendorContactLinks';
import { prerenderData } from '@/generated/prerenderData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to update page SEO dynamically
const updatePageSEO = (vendor) => {
  if (!vendor) return;
  
  const title = `${vendor.name} - ${vendor.category} | Gibraltar Wedding Expo`;
  const description = vendor.description?.substring(0, 160) || 
    `${vendor.name} - Professional ${vendor.category} in Gibraltar. Contact for wedding services and enquiries.`;
  
  document.title = title;
  
  // Update meta tags
  const updateMeta = (name, content, isProperty = false) => {
    const attr = isProperty ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attr}="${name}"]`);
    if (meta) {
      meta.setAttribute('content', content);
    } else {
      meta = document.createElement('meta');
      meta.setAttribute(attr, name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };
  
  updateMeta('description', description);
  updateMeta('robots', 'index, follow');
  updateMeta('og:title', title, true);
  updateMeta('og:description', description, true);
  updateMeta('og:type', 'business.business', true);
  updateMeta('twitter:card', 'summary_large_image');
  updateMeta('twitter:title', title);
  updateMeta('twitter:description', description);
  if (vendor.images?.[0]) {
    updateMeta('og:image', vendor.images[0], true);
    updateMeta('twitter:image', vendor.images[0]);
  }

  const canonicalHref = `${window.location.origin}${window.location.pathname}`;
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', canonicalHref);
  updateMeta('og:url', canonicalHref, true);
  updateMeta('twitter:url', canonicalHref);
  
  // Add structured data for local business
  let script = document.getElementById('vendor-structured-data');
  if (!script) {
    script = document.createElement('script');
    script.id = 'vendor-structured-data';
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": vendor.name,
    "description": vendor.description,
    "image": vendor.images?.[0] || vendor.logo,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": vendor.address,
      "addressLocality": "Gibraltar",
      "addressCountry": "GI"
    },
    "telephone": vendor.phone,
    "email": vendor.email,
    "url": vendor.website,
    "priceRange": vendor.price_range,
    "aggregateRating": vendor.review_count > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": vendor.average_rating,
      "reviewCount": vendor.review_count
    } : undefined
  };
  
  script.textContent = JSON.stringify(structuredData);
};

export default function VendorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, updateFavorites } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const hasVendorSession = !!localStorage.getItem('vendorToken');

  const isFavorited = user?.favorites?.includes(id);

  useEffect(() => {
    fetchVendor();
  }, [id]);

  // Update SEO when vendor data loads
  useEffect(() => {
    if (vendor) {
      updatePageSEO(vendor);
    }
    
    // Cleanup on unmount
    return () => {
      document.title = 'Gibraltar Wedding Expo - Find Wedding Vendors in Gibraltar';
      const script = document.getElementById('vendor-structured-data');
      if (script) script.remove();
    };
  }, [vendor]);

  useEffect(() => {
    if (!vendor?.slug || !id) return;
    if (id !== vendor.slug) {
      navigate(`/vendors/${vendor.slug}`, { replace: true });
    }
  }, [id, navigate, vendor]);

  const fetchVendor = async () => {
    const fallbackVendor = (prerenderData?.vendors || []).find(
      (candidate) => candidate?.id === id || candidate?.slug === id || candidate?._id === id,
    );

    try {
      const response = await axios.get(`${API}/vendors/${id}`);
      setVendor(response.data);
      axios.post(`${API}/track/pageview`, null, {
        params: {
          page: `/vendors/${id}`,
          vendor_id: response.data.id,
          page_label: `Vendor Profile: ${response.data.name}`,
        },
      }).catch(() => {});
    } catch (error) {
      console.error('Failed to fetch vendor:', error);
      if (fallbackVendor) {
        setVendor(fallbackVendor);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save favourites');
      return;
    }

    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updateFavorites(user.favorites.filter(fid => fid !== id));
        toast.success('Removed from favourites');
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updateFavorites([...user.favorites, id]);
        toast.success('Added to favourites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const nextLightboxImage = () => {
    if (!images.length || lightboxIndex === null) return;
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevLightboxImage = () => {
    if (!images.length || lightboxIndex === null) return;
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${i < Math.floor(rating) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-stone-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-stone-600 text-lg mb-4">Vendor not found</p>
        <Link to="/vendors">
          <Button variant="outline" className="btn-outline">Back to Vendors</Button>
        </Link>
      </div>
    );
  }

  const images = vendor.images?.length > 0
    ? [vendor.cover_image, ...vendor.images.filter((image) => image !== vendor.cover_image)].filter(Boolean)
    : [vendor.cover_image || 'https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/ixkrplkk_1971-gersonana-websized-319%20copy.jpg'];
  const emailHref = buildVendorEmailHref(vendor.email, vendor.name);
  const whatsappHref = buildVendorWhatsAppHref(vendor.whatsapp);

  return (
    <div className="min-h-screen py-24" data-testid="vendor-detail-page">
      <div className="container-wedding">
        {/* Back Button */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <Link
            to="/vendors"
            className="inline-flex items-center gap-2 text-stone-600 hover:text-[#5C0120] transition-colors duration-200"
            data-testid="vendor-detail-back-to-vendors-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Vendors
          </Link>
          {hasVendorSession && (
            <Link to="/vendor/dashboard" data-testid="vendor-detail-back-to-dashboard-link">
              <Button variant="outline" className="border-[#D4AF37] text-[#8f6b00] hover:bg-[#D4AF37]/10">
                <Store className="h-4 w-4 mr-2" />
                Back to Vendor Dashboard
              </Button>
            </Link>
          )}
        </div>

        <div className="space-y-8" data-testid="vendor-detail-content-stack">
          {/* Main Content */}
          <div className="space-y-8">
            {vendor.special_offer_text && (
              <div className="rounded-[22px] border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-5 py-4" data-testid="vendor-detail-special-offer-card">
                <p className="text-xs uppercase tracking-[0.18em] text-[#5C0120] mb-2">Special Offer</p>
                <h2 className="text-lg font-semibold text-[#5C0120]">{vendor.special_offer_text}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-stone-700">
                  {vendor.special_offer_code && <span className="rounded-full bg-white px-3 py-1 border border-stone-200">Code: {vendor.special_offer_code}</span>}
                  {vendor.special_offer_expires_at && <span>Ends: {vendor.special_offer_expires_at}</span>}
                  {vendor.special_offer_link && (
                    <a href={vendor.special_offer_link} target="_blank" rel="noopener noreferrer" className="text-[#5C0120] font-medium hover:underline" data-testid="vendor-special-offer-link">
                      View Offer
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Vendor Header Summary */}
            <div className="bg-white p-8 rounded-sm border border-stone-100 card-shadow" data-testid="vendor-detail-header-summary">
              <div className="mb-4 flex flex-wrap gap-2" data-testid="vendor-detail-categories">
                {(vendor.categories?.length > 0 ? vendor.categories : [vendor.category]).filter(Boolean).map((category) => (
                  <span key={category} className="caption !mb-0 rounded-full bg-[#D4AF37]/12 px-3 py-1 text-[#8f6b00]">
                    {category}
                  </span>
                ))}
              </div>

              <div className="flex items-end justify-between gap-4 mb-5">
                <div className="flex min-w-0 items-end gap-5" data-testid="vendor-detail-name-logo-wrap">
                  {vendor.logo && (
                    <div className="w-40 h-40 flex-shrink-0" data-testid="vendor-detail-logo-wrap">
                      <img
                        src={vendor.logo}
                        alt={`${vendor.name} logo`}
                        className="w-full h-full object-contain"
                        data-testid="vendor-detail-logo"
                      />
                    </div>
                  )}
                  <h1 className="heading-lg" data-testid="vendor-name">{vendor.name}</h1>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  onClick={handleFavorite}
                  data-testid="detail-favorite-btn"
                >
                  <Heart
                    className={`h-6 w-6 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-stone-400'}`}
                  />
                </Button>
              </div>

              <div className="flex items-center gap-4" data-testid="vendor-detail-rating-summary">
                <div className="flex items-center gap-1">
                  {renderStars(vendor.average_rating)}
                </div>
                <span className="text-stone-600">
                  {vendor.average_rating} ({vendor.review_count} reviews)
                </span>
                <span className="text-[#5C0120] font-semibold text-lg">{vendor.price_range}</span>
              </div>
            </div>

            {/* Image Gallery */}
            <div className="rounded-sm border border-stone-100 bg-white p-4 card-shadow" data-testid="vendor-detail-photo-grid-section">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="heading-md" data-testid="vendor-detail-photo-grid-title">Gallery</h2>
                <span className="text-xs text-stone-500" data-testid="vendor-detail-photo-count">{images.length} image{images.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="columns-2 md:columns-3 gap-3 [column-fill:_balance]" data-testid="vendor-detail-photo-grid">
                {images.map((image, index) => (
                  <button
                    key={`${vendor.id}-photo-grid-${index}`}
                    type="button"
                    onClick={() => setLightboxIndex(index)}
                    className="group mb-3 block w-full break-inside-avoid overflow-hidden rounded-md border border-stone-200 bg-stone-50 text-left transition-colors hover:border-[#D4AF37]"
                    data-testid={`vendor-detail-photo-grid-item-${index}`}
                  >
                    <img
                      src={image}
                      alt={`${vendor.name} photo ${index + 1}`}
                      className="w-full h-auto"
                    />
                  </button>
                ))}
              </div>

              {/* Featured Badge */}
              {vendor.is_featured && (
                <span className="inline-flex mt-4 bg-[#5C0120] text-white px-4 py-2 text-xs uppercase tracking-widest font-medium" data-testid="vendor-detail-featured-badge">
                  Featured Vendor
                </span>
              )}
            </div>

            {/* Vendor Info */}
            <div className="bg-white p-8 rounded-sm border border-stone-100 card-shadow">
              <p className="text-stone-600 leading-relaxed mb-8 whitespace-pre-wrap break-words" data-testid="vendor-description-text">
                {vendor.description}
              </p>

              {/* Video Section */}
              {vendor.video_url && (
                <div className="bg-stone-50 p-6 rounded-sm border border-stone-100 mb-8" data-testid="vendor-detail-video-section">
                  <h3 className="font-semibold mb-4" data-testid="vendor-detail-video-title">Video</h3>
                  <div className="aspect-video bg-black rounded-sm overflow-hidden">
                    <video 
                      src={vendor.video_url} 
                      controls 
                      className="w-full h-full"
                      poster={images[0]}
                      data-testid="vendor-detail-video-player"
                    />
                  </div>
                </div>
              )}

              {/* Priority Chat Section */}
              <div className="mb-8" data-testid="vendor-priority-chat-section">
                <div className="mb-4" data-testid="vendor-priority-chat-heading-wrap">
                  <h2 className="text-xl font-semibold text-stone-900" data-testid="vendor-priority-chat-title">Message {vendor.name} first</h2>
                  <p className="text-sm text-stone-600" data-testid="vendor-priority-chat-subtitle">
                    Start the conversation here to get fast replies and keep everything in one place.
                  </p>
                </div>
                <InquiryForm vendorId={id} vendorName={vendor.name} vendor={vendor} />
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-stone-50 rounded-sm" data-testid="vendor-direct-contact-section">
                {emailHref && (
                  <div className="flex items-center gap-3" data-testid="vendor-email-link-wrap">
                    <Mail className="h-5 w-5 text-[#D4AF37]" />
                    <a href={emailHref} className="text-stone-600 hover:text-[#5C0120]" data-testid="vendor-email-link">
                      {vendor.email}
                    </a>
                  </div>
                )}
                {whatsappHref && (
                  <div className="flex items-center gap-3">
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full hover:bg-[#20bd5a] transition-colors"
                      data-testid="whatsapp-button"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                  </div>
                )}
              </div>
            </div>

            {lightboxIndex !== null && (
              <div
                className="fixed inset-0 z-50 bg-black/80 p-4 md:p-8"
                data-testid="vendor-detail-photo-lightbox"
                onClick={() => setLightboxIndex(null)}
              >
                <div className="relative mx-auto h-full max-w-6xl flex items-center justify-center" onClick={(event) => event.stopPropagation()}>
                  <img
                    src={images[lightboxIndex]}
                    alt={`${vendor.name} enlarged photo ${lightboxIndex + 1}`}
                    className="max-h-[88vh] w-auto max-w-full object-contain"
                    data-testid="vendor-detail-photo-lightbox-image"
                  />

                  <button
                    type="button"
                    onClick={() => setLightboxIndex(null)}
                    className="absolute right-2 top-2 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-white"
                    data-testid="vendor-detail-photo-lightbox-close"
                  >
                    Close
                  </button>

                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={prevLightboxImage}
                        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-3 text-stone-700 hover:bg-white"
                        data-testid="vendor-detail-photo-lightbox-prev"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={nextLightboxImage}
                        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-3 text-stone-700 hover:bg-white"
                        data-testid="vendor-detail-photo-lightbox-next"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Map (if coordinates available) */}
            {vendor.latitude && vendor.longitude && (
              <div className="bg-white p-8 rounded-sm border border-stone-100 card-shadow">
                <h2 className="heading-md mb-6">Location</h2>
                <div className="rounded-sm overflow-hidden h-[300px] bg-stone-100">
                  <iframe
                    title="Vendor Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyCxSBzxOCVD_VRKMKq0D_LxVdZ6ij_v7OI&q=${vendor.latitude},${vendor.longitude}&zoom=15`}
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Reviews */}
            <div className="bg-white p-8 rounded-sm border border-stone-100 card-shadow" data-testid="vendor-review-section-last">
              <ReviewSection vendorId={id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
